import { type MutableRefObject, type ReactNode, useState } from "react"
import { Alert, Box, Button, Card, CardContent, CardHeader, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { AtSign, FileText, Mail, Save, CheckCircle2 } from "lucide-react"

import { type TargetSelection } from "@crm-src/components/email-target-selector"
import CampaignFileAttachment, { type CampaignAttachedFile } from "@crm-src/components/campaign-file-attachment"
import EmailLayoutSelector from "@crm-src/components/email-layout-selector"
import { OliveNewsletterBuilder } from "@crm-src/components/olive-newsletter-builder"
import SimpleTextEmailBuilder from "@crm-src/components/simple-text-email-builder"
import CeoLetterEmailBuilder from "@crm-src/components/ceo-letter-email-builder"
import { WebinarBuilder } from "@crm-src/components/webinar-builder"
import { MastheadNewsletterBuilder } from "@crm-src/components/masthead-newsletter-builder"
import { BrochureBuilder } from "@crm-src/components/brochure-builder"
import { UseCaseBrochureBuilder } from "@crm-src/components/usecase-brochure-builder"
import { SpotlightWebinarBuilder } from "@crm-src/components/spotlight-webinar-builder"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import { isKnownLayout, type EmailLayoutType } from "@crm-src/lib/email-layouts"
import { DEFAULT_CAMPAIGN_FROM, XOP_EMAIL_DOMAIN, validateFromAddress, validateReplyTo } from "@crm-src/lib/sender-fields"
import { CAMPAIGN_SECTIONS, SECTION_SCROLL_MARGIN_TOP } from "./sections"

interface CampaignState {
  subject: string
  content: string
  layout: EmailLayoutType | undefined
  target_selection: TargetSelection
  files: CampaignAttachedFile[]
  content_data: any
  from_address: string
  from_name: string
  reply_to: string
}

interface ToastFn {
  (opts: { title: string; description: string; variant?: "default" | "destructive" }): void
}

interface ContentEditorProps {
  campaign: CampaignState
  setCampaign: React.Dispatch<React.SetStateAction<CampaignState>>
  tenant: any
  existingCampaignId?: string
  isAutoSaving: boolean
  lastSaved: Date | null
  timeAgo: string
  onLayoutChange: (layoutId: EmailLayoutType) => void | Promise<void>
  onNewsletterUpdate: () => void
  toast: ToastFn
  // The "To" (Recipients) card, built by the form and rendered between From and
  // Body so the editor reads top-to-bottom: From → To → Body → Attachments.
  recipientsSlot?: ReactNode

  // Refs (mutable objects passed by reference)
  autoSaveEnabledRef: MutableRefObject<boolean>
  hasUserMadeChangesRef: MutableRefObject<boolean>
  lastAutoSavedContentRef: MutableRefObject<string>
  lastNewsletterContentRef: MutableRefObject<string>
  lastNewsletterSectionsRef: MutableRefObject<string>
  contentRef: MutableRefObject<string>
  contentDataRef: MutableRefObject<any>
  pendingNewsletterContentRef: MutableRefObject<string | null>
  pendingNewsletterSectionsRef: MutableRefObject<any[] | null>
  newsletterUpdateTimeoutRef: MutableRefObject<NodeJS.Timeout | null>
}

const sectionCardSx = { scrollMarginTop: `${SECTION_SCROLL_MARGIN_TOP}px` } as const

export default function ContentEditor({
  campaign,
  setCampaign,
  tenant,
  existingCampaignId,
  isAutoSaving,
  lastSaved,
  timeAgo,
  onLayoutChange,
  onNewsletterUpdate,
  toast: _toast,
  recipientsSlot,
  autoSaveEnabledRef,
  hasUserMadeChangesRef,
  lastAutoSavedContentRef,
  lastNewsletterContentRef,
  lastNewsletterSectionsRef,
  contentRef,
  contentDataRef,
  pendingNewsletterContentRef,
  pendingNewsletterSectionsRef,
  newsletterUpdateTimeoutRef,
}: ContentEditorProps) {
  // Inline sender validation (crm-api enforces the same rules authoritatively).
  const fromAddressError = validateFromAddress(campaign.from_address)
  const replyToError = validateReplyTo(campaign.reply_to)

  const markSenderEdited = () => {
    autoSaveEnabledRef.current = true
    hasUserMadeChangesRef.current = true
  }

  // #2 Changing the template on an existing campaign WIPES the content — gate it
  // behind a confirm dialog. (First-time selection applies immediately.)
  const [pendingLayout, setPendingLayout] = useState<EmailLayoutType | null>(null)

  const requestLayoutChange = (layoutId: EmailLayoutType) => {
    if (layoutId === campaign.layout) return
    if (campaign.layout) {
      setPendingLayout(layoutId)
      return
    }
    autoSaveEnabledRef.current = true
    hasUserMadeChangesRef.current = true
    onLayoutChange(layoutId)
  }

  const confirmLayoutChange = () => {
    if (!pendingLayout) return
    contentRef.current = ""
    contentDataRef.current = null
    lastAutoSavedContentRef.current = ""
    lastNewsletterContentRef.current = ""
    lastNewsletterSectionsRef.current = ""
    autoSaveEnabledRef.current = true
    hasUserMadeChangesRef.current = true
    onLayoutChange(pendingLayout)
    setCampaign((prev) => ({ ...prev, content: "", content_data: null }))
    setPendingLayout(null)
  }

  // The visual builder for the current template (used inside the Body section).
  // The persistence wiring every single-section builder shares: the HTML goes
  // to `content`, the sections to `content_data.sections`, both debounced into
  // one parent update. (The older builders below still spell it out inline.)
  const sectionBuilderProps = {
    tenantName: tenant?.name || 'Your Company',
    tenantInfo: tenant ? {
      name: tenant.name,
      logo_url: tenant.logo_url,
      address: tenant.address,
      city: tenant.city,
      state: tenant.state,
      postal_code: tenant.postal_code,
      country: tenant.country,
      website: tenant.website
    } : undefined,
    initialSections: campaign.content_data?.sections,
    onContentChange: (html: string) => {
      if (html === lastNewsletterContentRef.current) {
        return
      }
      lastNewsletterContentRef.current = html
      if (lastAutoSavedContentRef.current && html !== lastAutoSavedContentRef.current) {
        autoSaveEnabledRef.current = true
        hasUserMadeChangesRef.current = true
      }
      contentRef.current = html
      if (!lastAutoSavedContentRef.current) {
        lastAutoSavedContentRef.current = html
      }
      pendingNewsletterContentRef.current = html
      if (newsletterUpdateTimeoutRef.current) {
        clearTimeout(newsletterUpdateTimeoutRef.current)
      }
      newsletterUpdateTimeoutRef.current = setTimeout(() => {
        onNewsletterUpdate()
        newsletterUpdateTimeoutRef.current = null
      }, 100)
    },
    onSectionsChange: (sections: any[]) => {
      const sectionsJson = JSON.stringify(sections)
      if (sectionsJson === lastNewsletterSectionsRef.current) {
        return
      }
      lastNewsletterSectionsRef.current = sectionsJson
      contentDataRef.current = { sections }
      if (campaign.content_data) {
        autoSaveEnabledRef.current = true
        hasUserMadeChangesRef.current = true
      }
      pendingNewsletterSectionsRef.current = sections
      if (newsletterUpdateTimeoutRef.current) {
        clearTimeout(newsletterUpdateTimeoutRef.current)
      }
      newsletterUpdateTimeoutRef.current = setTimeout(() => {
        onNewsletterUpdate()
        newsletterUpdateTimeoutRef.current = null
      }, 100)
    },
  }

  // A template this build doesn't know (added after the desktop app was
  // bundled — see isKnownLayout). Falling through to the newsletter builder
  // would re-render the campaign as a newsletter and auto-save over its real
  // content, so show a notice and leave the stored content untouched.
  const unknownLayout = !!campaign.layout && !isKnownLayout(campaign.layout)

  const visualBuilder = unknownLayout ? (
    <Alert severity="warning" data-testid="unknown-layout-notice">
      This campaign uses the “{String(campaign.layout)}” template, which this version of the
      app doesn’t have yet. Update the app (or open the campaign in the browser) to edit its
      content — nothing here has been changed.
    </Alert>
  ) : campaign.layout === 'simple_text' ? (
    <SimpleTextEmailBuilder
      tenantInfo={tenant ? {
        name: tenant.name,
        logo_url: tenant.logo_url,
        address: tenant.address,
        phone: tenant.phone,
        email: tenant.email,
        website: tenant.website
      } : undefined}
      initialContent={campaign.content}
      onContentChange={(html) => {
        if (lastAutoSavedContentRef.current && html !== lastAutoSavedContentRef.current) {
          autoSaveEnabledRef.current = true
          hasUserMadeChangesRef.current = true
        } else if (!lastAutoSavedContentRef.current) {
        }
        contentRef.current = html
        setCampaign(prev => ({ ...prev, content: html }))
        if (!lastAutoSavedContentRef.current) {
          lastAutoSavedContentRef.current = html
        }
      }}
    />
  ) : campaign.layout === 'ceo_letter' ? (
    <CeoLetterEmailBuilder
      initialContent={campaign.content}
      onContentChange={(html) => {
        if (lastAutoSavedContentRef.current && html !== lastAutoSavedContentRef.current) {
          autoSaveEnabledRef.current = true
          hasUserMadeChangesRef.current = true
        }
        contentRef.current = html
        setCampaign(prev => ({ ...prev, content: html }))
        if (!lastAutoSavedContentRef.current) {
          lastAutoSavedContentRef.current = html
        }
      }}
    />
  ) : campaign.layout === 'webinar' ? (
    <WebinarBuilder
      tenantName={tenant?.name || 'Your Company'}
      tenantLogo={tenant?.logo_url}
      tenantInfo={tenant ? {
        name: tenant.name,
        logo_url: tenant.logo_url,
        address: tenant.address,
        city: tenant.city,
        state: tenant.state,
        postal_code: tenant.postal_code,
        country: tenant.country,
        phone: tenant.phone,
        email: tenant.email,
        website: tenant.website
      } : undefined}
      initialSections={campaign.content_data?.sections}
      onContentChange={(html) => {
        if (html === lastNewsletterContentRef.current) {
          return
        }
        lastNewsletterContentRef.current = html
        if (lastAutoSavedContentRef.current && html !== lastAutoSavedContentRef.current) {
          autoSaveEnabledRef.current = true
          hasUserMadeChangesRef.current = true
        } else if (!lastAutoSavedContentRef.current) {
        }
        contentRef.current = html
        if (!lastAutoSavedContentRef.current) {
          lastAutoSavedContentRef.current = html
        }
        pendingNewsletterContentRef.current = html
        if (newsletterUpdateTimeoutRef.current) {
          clearTimeout(newsletterUpdateTimeoutRef.current)
        }
        newsletterUpdateTimeoutRef.current = setTimeout(() => {
          onNewsletterUpdate()
          newsletterUpdateTimeoutRef.current = null
        }, 100)
      }}
      onSectionsChange={(sections) => {
        const sectionsJson = JSON.stringify(sections)
        if (sectionsJson === lastNewsletterSectionsRef.current) {
          return
        }
        lastNewsletterSectionsRef.current = sectionsJson
        contentDataRef.current = { sections }
        pendingNewsletterSectionsRef.current = sections
        if (newsletterUpdateTimeoutRef.current) {
          clearTimeout(newsletterUpdateTimeoutRef.current)
        }
        newsletterUpdateTimeoutRef.current = setTimeout(() => {
          onNewsletterUpdate()
          newsletterUpdateTimeoutRef.current = null
        }, 100)
      }}
    />
  ) : campaign.layout === 'masthead_newsletter' ? (
    <MastheadNewsletterBuilder
      tenantName={tenant?.name || 'Your Company'}
      tenantInfo={tenant ? {
        name: tenant.name,
        logo_url: tenant.logo_url,
        address: tenant.address,
        city: tenant.city,
        state: tenant.state,
        postal_code: tenant.postal_code,
        country: tenant.country,
        phone: tenant.phone,
        email: tenant.email,
        website: tenant.website
      } : undefined}
      initialSections={campaign.content_data?.sections}
      onContentChange={(html) => {
        if (html === lastNewsletterContentRef.current) {
          return
        }
        lastNewsletterContentRef.current = html
        if (lastAutoSavedContentRef.current && html !== lastAutoSavedContentRef.current) {
          autoSaveEnabledRef.current = true
          hasUserMadeChangesRef.current = true
        }
        contentRef.current = html
        if (!lastAutoSavedContentRef.current) {
          lastAutoSavedContentRef.current = html
        }
        pendingNewsletterContentRef.current = html
        if (newsletterUpdateTimeoutRef.current) {
          clearTimeout(newsletterUpdateTimeoutRef.current)
        }
        newsletterUpdateTimeoutRef.current = setTimeout(() => {
          onNewsletterUpdate()
          newsletterUpdateTimeoutRef.current = null
        }, 100)
      }}
      onSectionsChange={(sections) => {
        const sectionsJson = JSON.stringify(sections)
        if (sectionsJson === lastNewsletterSectionsRef.current) {
          return
        }
        lastNewsletterSectionsRef.current = sectionsJson
        contentDataRef.current = { sections }
        pendingNewsletterSectionsRef.current = sections
        if (newsletterUpdateTimeoutRef.current) {
          clearTimeout(newsletterUpdateTimeoutRef.current)
        }
        newsletterUpdateTimeoutRef.current = setTimeout(() => {
          onNewsletterUpdate()
          newsletterUpdateTimeoutRef.current = null
        }, 100)
      }}
    />
  ) : campaign.layout === 'brochure' ? (
    <BrochureBuilder
      tenantName={tenant?.name || 'Your Company'}
      tenantInfo={tenant ? {
        name: tenant.name,
        logo_url: tenant.logo_url,
        address: tenant.address,
        city: tenant.city,
        state: tenant.state,
        postal_code: tenant.postal_code,
        country: tenant.country,
        phone: tenant.phone,
        email: tenant.email,
        website: tenant.website
      } : undefined}
      initialSections={campaign.content_data?.sections}
      onContentChange={(html) => {
        if (html === lastNewsletterContentRef.current) {
          return
        }
        lastNewsletterContentRef.current = html
        if (lastAutoSavedContentRef.current && html !== lastAutoSavedContentRef.current) {
          autoSaveEnabledRef.current = true
          hasUserMadeChangesRef.current = true
        }
        contentRef.current = html
        if (!lastAutoSavedContentRef.current) {
          lastAutoSavedContentRef.current = html
        }
        pendingNewsletterContentRef.current = html
        if (newsletterUpdateTimeoutRef.current) {
          clearTimeout(newsletterUpdateTimeoutRef.current)
        }
        newsletterUpdateTimeoutRef.current = setTimeout(() => {
          onNewsletterUpdate()
          newsletterUpdateTimeoutRef.current = null
        }, 100)
      }}
      onSectionsChange={(sections) => {
        const sectionsJson = JSON.stringify(sections)
        if (sectionsJson === lastNewsletterSectionsRef.current) {
          return
        }
        lastNewsletterSectionsRef.current = sectionsJson
        contentDataRef.current = { sections }
        if (campaign.content_data) {
          autoSaveEnabledRef.current = true
          hasUserMadeChangesRef.current = true
        }
        pendingNewsletterSectionsRef.current = sections
        if (newsletterUpdateTimeoutRef.current) {
          clearTimeout(newsletterUpdateTimeoutRef.current)
        }
        newsletterUpdateTimeoutRef.current = setTimeout(() => {
          onNewsletterUpdate()
          newsletterUpdateTimeoutRef.current = null
        }, 100)
      }}
    />
  ) : campaign.layout === 'use_case_brochure' ? (
    <UseCaseBrochureBuilder
      tenantName={tenant?.name || 'Your Company'}
      tenantInfo={tenant ? {
        name: tenant.name,
        logo_url: tenant.logo_url,
        address: tenant.address,
        city: tenant.city,
        state: tenant.state,
        postal_code: tenant.postal_code,
        country: tenant.country,
        phone: tenant.phone,
        email: tenant.email,
        website: tenant.website
      } : undefined}
      initialSections={campaign.content_data?.sections}
      onContentChange={(html) => {
        if (html === lastNewsletterContentRef.current) {
          return
        }
        lastNewsletterContentRef.current = html
        if (lastAutoSavedContentRef.current && html !== lastAutoSavedContentRef.current) {
          autoSaveEnabledRef.current = true
          hasUserMadeChangesRef.current = true
        }
        contentRef.current = html
        if (!lastAutoSavedContentRef.current) {
          lastAutoSavedContentRef.current = html
        }
        pendingNewsletterContentRef.current = html
        if (newsletterUpdateTimeoutRef.current) {
          clearTimeout(newsletterUpdateTimeoutRef.current)
        }
        newsletterUpdateTimeoutRef.current = setTimeout(() => {
          onNewsletterUpdate()
          newsletterUpdateTimeoutRef.current = null
        }, 100)
      }}
      onSectionsChange={(sections) => {
        const sectionsJson = JSON.stringify(sections)
        if (sectionsJson === lastNewsletterSectionsRef.current) {
          return
        }
        lastNewsletterSectionsRef.current = sectionsJson
        contentDataRef.current = { sections }
        if (campaign.content_data) {
          autoSaveEnabledRef.current = true
          hasUserMadeChangesRef.current = true
        }
        pendingNewsletterSectionsRef.current = sections
        if (newsletterUpdateTimeoutRef.current) {
          clearTimeout(newsletterUpdateTimeoutRef.current)
        }
        newsletterUpdateTimeoutRef.current = setTimeout(() => {
          onNewsletterUpdate()
          newsletterUpdateTimeoutRef.current = null
        }, 100)
      }}
    />
  ) : campaign.layout === 'spotlight_webinar' ? (
    <SpotlightWebinarBuilder {...sectionBuilderProps} />
  ) : (
    <OliveNewsletterBuilder
      preset={campaign.layout === 'case_study' ? 'case_study' : 'newsletter'}
      tenantName={tenant?.name || 'Your Company'}
      tenantLogo={tenant?.logo_url}
      tenantInfo={tenant ? {
        name: tenant.name,
        logo_url: tenant.logo_url,
        address: tenant.address,
        city: tenant.city,
        state: tenant.state,
        postal_code: tenant.postal_code,
        country: tenant.country,
        phone: tenant.phone,
        email: tenant.email,
        website: tenant.website
      } : undefined}
      initialSections={campaign.content_data?.sections}
      onContentChange={(html) => {
        if (html === lastNewsletterContentRef.current) {
          return
        }
        lastNewsletterContentRef.current = html
        if (lastAutoSavedContentRef.current && html !== lastAutoSavedContentRef.current) {
          autoSaveEnabledRef.current = true
          hasUserMadeChangesRef.current = true
        } else if (!lastAutoSavedContentRef.current) {
        }
        contentRef.current = html
        if (!lastAutoSavedContentRef.current) {
          lastAutoSavedContentRef.current = html
        }
        pendingNewsletterContentRef.current = html
        if (newsletterUpdateTimeoutRef.current) {
          clearTimeout(newsletterUpdateTimeoutRef.current)
        }
        newsletterUpdateTimeoutRef.current = setTimeout(() => {
          onNewsletterUpdate()
          newsletterUpdateTimeoutRef.current = null
        }, 100)
      }}
      onSectionsChange={(sections) => {
        const sectionsJson = JSON.stringify(sections)
        if (sectionsJson === lastNewsletterSectionsRef.current) {
          return
        }
        lastNewsletterSectionsRef.current = sectionsJson
        contentDataRef.current = { sections }
        if (campaign.content_data) {
          autoSaveEnabledRef.current = true
          hasUserMadeChangesRef.current = true
        } else {
        }
        pendingNewsletterSectionsRef.current = sections
        if (newsletterUpdateTimeoutRef.current) {
          clearTimeout(newsletterUpdateTimeoutRef.current)
        }
        newsletterUpdateTimeoutRef.current = setTimeout(() => {
          onNewsletterUpdate()
          newsletterUpdateTimeoutRef.current = null
        }, 100)
      }}
    />
  )

  const builderHint =
    unknownLayout
      ? ''
      : campaign.layout === 'simple_text'
      ? '' // chrome (logo/greeting/footer) is shown in the live preview — no redundant note
      : campaign.layout === 'ceo_letter'
      ? '' // greeting shown in the live preview — no redundant note
      : campaign.layout === 'webinar'
      ? 'Customize your webinar invitation: edit banner, title, event details, and description'
      : campaign.layout === 'case_study'
      ? 'Tell the customer success story: title image, customer, problem, XOP solution, network diagram, and benefits'
      : campaign.layout === 'masthead_newsletter'
      ? 'Build a branded masthead webinar invite: announcement bar, title, hero + sponsor, date & register, body, and footer links'
      : campaign.layout === 'brochure'
      ? 'Craft a modern brochure: edit the hero, the emoji feature highlights, an image showcase, and the closing call-to-action'
      : campaign.layout === 'use_case_brochure'
      ? 'Build a photo-led use-case brochure: edit the hero, the challenge, the two-column comparison, and the two-step solution flow'
      : campaign.layout === 'spotlight_webinar'
      ? 'Build the house webinar invite: pick a palette or your own colours, swap the logo, banner and speaker photos, then fill in the title, topics, date and speakers'
      : 'Click on any section to edit text, change colors, add or remove sections'

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {!campaign.layout ? (
        // ===== No template chosen → a clean, single-purpose template picker. =====
        <Card>
          <CardHeader
            title={
              <Box>
                <Typography component="span" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
                  <Mail size={20} />
                  Create your email campaign
                </Typography>
                <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
                  Start by choosing a template — the rest unlocks once you pick one.
                </Typography>
              </Box>
            }
          />
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography component="label" sx={{ fontSize: 16, fontWeight: 600, mb: 1, display: "block" }}>
                Step 1 — Choose your email template
              </Typography>
              <EmailLayoutSelector selectedLayout={campaign.layout} onLayoutChange={requestLayoutChange} />
            </Box>
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Box
                sx={{
                  mx: "auto",
                  display: "flex",
                  height: 64,
                  width: 64,
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                  color: "primary.main",
                  mb: 2,
                }}
              >
                <FileText size={32} />
              </Box>
              <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary", mb: 1 }}>
                Pick a template to begin
              </Typography>
              <Typography sx={{ fontSize: 14, color: "text.secondary", maxWidth: 448, mx: "auto" }}>
                Each template is optimized for a different type of email. Once you choose one, you'll set the sender, recipients, content, and attachments.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        // ===== Template chosen → From / To / Body / Attachments sections. =====
        <>
          {/* Slim template bar — change is allowed (warns it clears content). */}
          <Card>
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Typography component="label" sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary", whiteSpace: "nowrap" }}>
                  Template
                </Typography>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <EmailLayoutSelector selectedLayout={campaign.layout} onLayoutChange={requestLayoutChange} />
                </Box>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  Changing the template clears the content.
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* FROM */}
          <Card id={CAMPAIGN_SECTIONS[0]!.id} sx={sectionCardSx}>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
                  <AtSign size={20} />
                  From
                </Box>
              }
              subheader="Who this email is sent from"
            />
            <CardContent>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.5 }}>
                <TextField
                  label="From name"
                  size="small"
                  value={campaign.from_name}
                  onChange={(e) => {
                    markSenderEdited()
                    setCampaign({ ...campaign, from_name: e.target.value })
                  }}
                  placeholder={tenant?.company_name || tenant?.name || "Your Company"}
                  helperText="Shown as the sender name"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="From email"
                  size="small"
                  value={campaign.from_address}
                  onChange={(e) => {
                    markSenderEdited()
                    setCampaign({ ...campaign, from_address: e.target.value })
                  }}
                  placeholder={DEFAULT_CAMPAIGN_FROM}
                  error={!!fromAddressError}
                  helperText={fromAddressError || `Any @${XOP_EMAIL_DOMAIN} address`}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Reply-to"
                  size="small"
                  value={campaign.reply_to}
                  onChange={(e) => {
                    markSenderEdited()
                    setCampaign({ ...campaign, reply_to: e.target.value })
                  }}
                  placeholder={campaign.from_address || DEFAULT_CAMPAIGN_FROM}
                  error={!!replyToError}
                  helperText={replyToError || "Defaults to the From address"}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* TO — the Recipients card, supplied by the form. */}
          {recipientsSlot}

          {/* BODY */}
          <Card id={CAMPAIGN_SECTIONS[2]!.id} sx={sectionCardSx}>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                  <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
                    <Mail size={20} />
                    Body
                  </Box>
                  <Box sx={{ fontSize: 12, color: "text.secondary", minWidth: 140, textAlign: "right" }}>
                    {isAutoSaving ? (
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            animation: "pulse 2s cubic-bezier(0.4,0,0.6,1) infinite",
                            "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.4 } },
                          }}
                        >
                          <Save size={12} />
                        </Box>
                        <span>Auto-saving...</span>
                      </Box>
                    ) : lastSaved ? (
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: "success.main" }}>
                        <CheckCircle2 size={12} />
                        <span>Saved {timeAgo || 'a moment ago'}</span>
                      </Box>
                    ) : (
                      <Box component="span" sx={{ opacity: 0 }}>Saved</Box>
                    )}
                  </Box>
                </Box>
              }
            />
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Subject */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography component="label" htmlFor="subject" sx={{ fontSize: 14, fontWeight: 500 }}>Email Subject</Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <InlineAiGenerator
                      fieldType="short"
                      context="email subject line for newsletter"
                      placeholder="e.g., exciting product update announcement"
                      currentValue={campaign.subject}
                      onGenerate={(content) => {
                        autoSaveEnabledRef.current = true
                        hasUserMadeChangesRef.current = true
                        setCampaign({ ...campaign, subject: content })
                      }}
                    />
                    <AiTextImprover
                      fieldType="short"
                      context="email subject line"
                      currentText={campaign.subject}
                      onImprove={(content) => {
                        autoSaveEnabledRef.current = true
                        hasUserMadeChangesRef.current = true
                        setCampaign({ ...campaign, subject: content })
                      }}
                    />
                  </Box>
                </Box>
                <TextField
                  id="subject"
                  fullWidth
                  size="small"
                  value={campaign.subject}
                  onChange={(e) => {
                    autoSaveEnabledRef.current = true
                    hasUserMadeChangesRef.current = true
                    setCampaign({ ...campaign, subject: e.target.value })
                  }}
                  placeholder="e.g., Welcome to our CRM platform!"
                  required
                />
              </Box>

              {/* Visual builder */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {builderHint && (
                  <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 1 }}>
                    {builderHint}
                  </Typography>
                )}
                {visualBuilder}
              </Box>
            </CardContent>
          </Card>

          {/* ATTACHMENTS */}
          <Card id={CAMPAIGN_SECTIONS[3]!.id} sx={sectionCardSx}>
            <CardHeader
              title={
                <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
                  <FileText size={20} />
                  Attachments
                </Box>
              }
              subheader="Add files to your email campaign"
            />
            <CardContent>
              <CampaignFileAttachment
                campaignId={existingCampaignId}
                files={campaign.files}
                onChange={(files) => {
                  autoSaveEnabledRef.current = true
                  hasUserMadeChangesRef.current = true
                  setCampaign({ ...campaign, files })
                }}
              />
            </CardContent>
          </Card>
        </>
      )}

      {/* #2 Confirm template change — it discards the current email content. */}
      <Dialog open={!!pendingLayout} onClose={() => setPendingLayout(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Change template?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Switching templates will <strong>clear all of this email's content</strong> and start
            the new template fresh. This can't be undone. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setPendingLayout(null)}>Keep current template</Button>
          <Button color="error" variant="contained" onClick={confirmLayoutChange}>
            Change &amp; clear content
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
